import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import { formatQueryParams } from '@utils/Format'
import { comunicacao as endpoint } from '@modules/solicitacoes/constants/URL'
import {
  COMUNICACAO_MODE,
  RESPOSTAS_RAPIDAS_FIXTURE,
} from '@modules/solicitacoes/constants/comunicacao'

const FALLBACK_MESSAGE = 'Erro inesperado'

// Paginação default do backend (resource_*_externa: _DEFAULT_PAGINA/_TAMANHO).
const DEFAULT_PAGINA = 1
const DEFAULT_TAMANHO = 50

// Clone defensivo: evita expor as fixtures (arrays/objetos vivos) a mutações
// acidentais pelos consumidores — replica o isolamento de uma resposta HTTP.
function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

// Converte o base64 do backend (download de mídia) num Blob que o
// chat-media-preview consome (URL.createObjectURL) para render inline de
// imagem/áudio/vídeo/documento. O backend responde base64 (padrão AAWZ, sem
// stream/presigned) — por isso NÃO usamos `responseType: 'blob'`; buscamos o
// JSON via `con.get` e reconstruímos o Blob aqui, com o mime_type informado.
function base64ToBlob(base64, mimeType) {
  const binary = atob(base64 || '')
  const length = binary.length
  const bytes = new Uint8Array(length)
  for (let i = 0; i < length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return new Blob([bytes], { type: mimeType || 'application/octet-stream' })
}

// ------------------------------------------------------------------ //
// Mapeamento backend (visão externa/interna) → shape do atendimento (store).
// O store/componentes consomem a shape do `atendimento` (clone fiel). O
// backend entrega um subconjunto enxuto (ConversaView / MensagemView, sem
// ids internos sensíveis — RF17). Aqui só normalizamos a FORMA: preservamos
// o que o backend tem e preenchemos com defaults seguros os campos de
// exibição que ele ainda não expõe (lidos defensivamente pelos componentes —
// ex.: contato ausente vira "Desconhecido"). NÃO inventamos dados de negócio.
// ------------------------------------------------------------------ //

// ConversaView → linha de conversa na shape do atendimento.
function mapConversaView(c) {
  const item = c || {}
  return {
    id: item.id,
    id_conexao: item.id_conexao || null,
    // `canal` decide se o input destrava: 'plataforma' sempre permite enviar
    // (sem depender de WhatsApp pareado). Sem ele, o chat-window cai no check
    // de conexões e trava o input na visão do cliente (que não lista conexões).
    canal: item.canal || null,
    status: item.status || 'aberta',
    mensagens_nao_lidas: item.mensagens_nao_lidas || 0,
    // Campos de exibição ainda não expostos pela visão externa — defaults
    // seguros para os getters defensivos do conversation-list-item.
    ultima_mensagem_em: item.ultima_mensagem_em || '',
    ultima_mensagem_preview: item.ultima_mensagem_preview || '',
    ultima_mensagem_is_note: item.ultima_mensagem_is_note || false,
    assigned_to_nome: item.assigned_to_nome || '',
    // Na visão do cliente, a conversa é "com um time AAWZ": o backend envia
    // `contato_nome` (= nome da conexão/time). Montamos o `contato` na shape do
    // atendimento para o conversation-list-item exibir o nome do time.
    contato: mapContatoFromConversa(item),
    // Subtítulo da solicitação vinculada (só conversas 1:1 — US-5). O backend
    // envia em ambas as visões (externa E interna); sem isso, o
    // conversation-list-item não tem como diferenciar duas conversas com o
    // mesmo título de conexão (ex.: duas solicitações "Jurídico").
    solicitacao_titulo: item.solicitacao_titulo || '',
    solicitacao_codigo: item.solicitacao_codigo || '',
    // Identidade da solicitação vinculada (uuid ou null). US-6: o detalhe da
    // solicitação (visão interna) usa este campo para achar/abrir a conversa
    // 1:1 correspondente sem depender de solicitacao_codigo (display-only).
    id_solicitacao: item.id_solicitacao || null,
  }
}

// Deriva o `contato` (shape do atendimento) a partir do que a visão externa
// expõe: prioriza um `contato` explícito; senão usa `contato_nome` (nome do
// time). Sem nome → null (o item cai no fallback defensivo "Desconhecido").
function mapContatoFromConversa(item) {
  if (item.contato) return item.contato
  if (item.contato_nome) {
    return {
      is_group: false,
      nome: item.contato_nome,
      nome_contato: item.contato_nome,
    }
  }
  return null
}

// Campos de anexo (contrato ampliado da MensagemView externa — passo 3.4),
// normalizados para o shape que store/chat-media-preview consomem. Extraído de
// mapMensagemView para manter a complexidade ciclomática da função baixa.
function mapMediaFields(item) {
  return {
    media_url: item.media_url || null,
    media_mime_type: item.media_mime_type || null,
    media_filename: item.media_filename || null,
    media_size_bytes: item.media_size_bytes || null,
  }
}

// Monta o bloco de mídia do payload de envio (ou null quando é texto). Só há
// mídia quando `media_url` está presente; nesse caso o `tipo` cai em
// 'documento' como default seguro.
function buildMediaPayload({
  tipo,
  media_url,
  media_mime_type,
  media_filename,
  media_size_bytes,
} = {}) {
  if (!media_url) return null
  return {
    tipo: tipo || 'documento',
    media_url,
    media_mime_type,
    media_filename,
    media_size_bytes,
  }
}

// Monta a bolha de saída (input do mapMensagemView) a partir do
// EnvioMensagemResult do servidor + inputs do envio. `result` = r.value.
function buildBolhaSaida({ result, conteudo, media } = {}) {
  return {
    id: result.id_mensagem,
    direcao: 'saida',
    canal: result.canal || null,
    tipo: (media && media.tipo) || 'texto',
    conteudo: conteudo || '',
    data_mensagem: new Date().toISOString(),
    is_note: false,
    status: result.transporte_acionado ? 'enviada' : 'pendente',
    ...(media || {}),
  }
}

// MensagemView → bolha de mensagem na shape do atendimento. O store/merge usa
// `id_conversa` e `reacoes` (não presentes no MensagemView) — normalizamos.
function mapMensagemView(m, id_conversa) {
  const item = m || {}
  return {
    id: item.id,
    id_conversa: id_conversa || null,
    direcao: item.direcao,
    canal: item.canal || null,
    tipo: item.tipo || 'texto',
    conteudo: item.conteudo || '',
    data_mensagem: item.data_mensagem || null,
    whatsapp_message_id: item.whatsapp_message_id || null,
    is_note: item.is_note || false,
    status: item.status || null,
    reacoes: item.reacoes || [],
    // O chat-message resolve a mídia por `id` (buildMediaUrl) e usa mime/filename
    // para decidir o tipo de preview; media_url/size seguem o shape do store.
    ...mapMediaFields(item),
    // Nome do atendente que enviou — o chat-message exibe como sender-name para
    // o cliente saber QUEM da equipe respondeu (mensagens do time).
    remetente_nome: item.remetente_nome || null,
  }
}

// Solicitação do núcleo → shape consumido pelo painel de detalhes. O backend
// entrega { id, codigo, titulo, area, status, responsavel_id, responsavel_nome,
// data_inicio, nota }. Preservamos o que o painel usa e aplicamos defaults
// seguros (responsavel_nome/nota podem vir null). `id` (uuid) é a identidade
// estável da solicitação — usada para salvar a nota; `codigo` é só display.
function mapSolicitacao(s) {
  const item = s || {}
  return {
    id: item.id,
    codigo: item.codigo || '',
    titulo: item.titulo || '',
    area: item.area || '',
    status: item.status || '',
    responsavel_nome: item.responsavel_nome || '',
    // PENDENTE BACKEND (item 16): profissional que abriu a solicitação. O núcleo
    // ainda não envia este campo; default '' mantém a linha "Criado por" oculta
    // no painel (hideWhenEmpty) até o backend expor `criado_por_nome`.
    criado_por_nome: item.criado_por_nome || '',
    data_inicio: item.data_inicio || '',
    nota: item.nota || '',
  }
}

// US-5: o painel de solicitações passa a receber um `value` polimórfico
// sinalizado por `legado`:
//   1:1    → { legado: false, solicitacao: <obj|null> }   (uma solicitação)
//   legado → { legado: true,  solicitacoes: [<obj>...] }   (accordion, herdado)
// Normaliza sempre para o shape único consumido pela view/painel:
//   { legado, solicitacao|null, solicitacoes[] }
// Fallback defensivo: se `value` vier como array cru (deploy transitório ou
// cache antigo pré-US-5), trata como legado — o deploy é coordenado no mesmo PR,
// mas o fallback é barato e evita tela quebrada.
function normalizarPainelSolicitacoes(value) {
  if (Array.isArray(value)) {
    return {
      legado: true,
      solicitacao: null,
      solicitacoes: value.map(mapSolicitacao),
    }
  }
  const v = value || {}
  return {
    legado: !!v.legado,
    solicitacao: v.solicitacao ? mapSolicitacao(v.solicitacao) : null,
    solicitacoes: Array.isArray(v.solicitacoes)
      ? v.solicitacoes.map(mapSolicitacao)
      : [],
  }
}

// Resposta crua de `con.*` (já é `response.data`: { status, message, value }).
// Normaliza o fallback consistente da camada de API.
function asResponse(r) {
  return r || { status: 500, message: FALLBACK_MESSAGE, value: null }
}

// Contrato idêntico ao apiAtendimento — mesmos métodos, mesma shape de
// retorno `{ status, message, value }`. As rotas da visão externa do
// profissional (listarConversas, listarMensagens, enviarMensagem,
// listarSolicitacoesDaConversa, salvar/carregar nota) chamam o backend real via
// `con`; os demais resolvem fixture (features WhatsApp-internas N/A na visão
// externa). A escolha fixture-vs-`con` fica isolada AQUI (endpoint isolation,
// AC11): nenhum componente importa URL.js nem connection.
class ApiComunicacao extends APITemplate {
  constructor() {
    super()
    // Modo ativo da view (generator | responder). A view o define em `created`
    // via setActiveMode() (store/actions.setMode). Serve de fallback para os
    // métodos AINDA em fixture chamados SEM `mode` explícito. Default =
    // generator. As rotas reais da visão externa ignoram o modo (identidade vem
    // da sessão server-side).
    this.activeMode = COMUNICACAO_MODE.GENERATOR
  }

  // Resolve o modo efetivo: o `mode` explícito do chamador tem prioridade;
  // na ausência (poller/serviço direto), cai no activeMode definido pela view.
  resolveMode(mode) {
    return mode || this.activeMode
  }

  setActiveMode(mode) {
    if (mode) this.activeMode = mode
  }

  ok(value, message = 'OK') {
    return this.buildSuccessResponse({ value, message })
  }

  // REAL: identidade da mídia para o chat-media-preview. O `chat-message`
  // resolve a URL por `id` da mensagem e a repassa de volta a `fetchMediaBlob`
  // como token opaco — devolvemos o path relativo do endpoint de download (o
  // `con.get` já prefixa o baseURL do axios).
  buildMediaUrl(id_mensagem) {
    if (!id_mensagem) return null
    // Roteia por `activeMode`: o atendente (RESPONDER) baixa pelo endpoint interno
    // (escopo por conexão); o cliente (GENERATOR) pelo externo (escopo por contato).
    return this.resolveMode() === COMUNICACAO_MODE.RESPONDER
      ? endpoint.mediaDownloadInterna(id_mensagem)
      : endpoint.mediaDownload(id_mensagem)
  }

  // REAL: baixa a mídia (base64 fail-closed) e reconstrói o Blob que o
  // chat-media-preview consome para render inline. Backend responde
  // { base64, name, extension, mime_type } — buscamos o JSON via `con.get`
  // (NÃO `responseType: 'blob'`) e montamos o Blob com o mime informado.
  async fetchMediaBlob({ url } = {}) {
    if (!url) {
      return this.buildErrorResponse({
        message: 'Mídia inválida para download.',
        value: null,
      })
    }
    const r = asResponse(await con.get(url))
    if (r.status === 200 && r.value && r.value.base64) {
      const blob = base64ToBlob(r.value.base64, r.value.mime_type)
      return this.ok({ blob }, r.message || 'OK')
    }
    return this.buildErrorResponse({
      message: r.message || 'Não foi possível carregar a mídia.',
      value: null,
    })
  }

  async listarConexoes({ mode } = {}) {
    // RESPONDER (interno): a visão do atendente PRECISA das conexões do
    // escritório (os times) pra renderizar e listar as conversas — sem elas
    // cai no empty state "Configure uma conexão". Puxa as conexões reais do
    // atendimento (mesmo tenant da sessão = interno).
    if (this.resolveMode(mode) === COMUNICACAO_MODE.RESPONDER) {
      const r = asResponse(await con.get(endpoint.conexoesAtendimento()))
      if (r.status === 200 && r.value) {
        const lista = Array.isArray(r.value) ? r.value : r.value.conexoes || []
        return this.ok(lista, r.message || 'Conexões carregadas.')
      }
      return this.ok([], 'Conexões carregadas.')
    }
    // GENERATOR (cliente): lista VAZIA de propósito — as conversas do cliente
    // referenciam conexões do tenant interno (cross-tenant), que o cliente não
    // lista. O computed conversasVivas só filtra quando há conexões carregadas;
    // com [] ele NÃO filtra e as conversas reais aparecem todas.
    await Promise.resolve()
    return this.ok([], 'Conexões carregadas.')
  }

  async syncContatos() {
    // N/A na visão externa: sync de contatos é feature WhatsApp-interna (a definir).
    await Promise.resolve()
    return this.ok(null, 'Sincronização de contatos iniciada.')
  }

  async syncChats() {
    // N/A na visão externa: sync de chats é feature WhatsApp-interna (a definir).
    await Promise.resolve()
    return this.ok(null, 'Sincronização de conversas iniciada.')
  }

  async refreshChatsList() {
    // N/A na visão externa: refresh de chats é feature WhatsApp-interna (a definir).
    await Promise.resolve()
    return this.ok(
      { novos_grupos: 0, novos_contatos: 0 },
      'Lista de conversas atualizada.'
    )
  }

  // REAL (visão externa): GET /v2/api/requisicoes/conversas/externa. A identidade
  // vem da sessão no backend (anti-IDOR) — só enviamos paginação. O modo da view
  // não viaja para o servidor (é client-side); é ignorado aqui. Mapeia
  // ConversaView[] → shape do atendimento que o store/componentes consomem.
  async listarConversas({
    pagina = DEFAULT_PAGINA,
    tamanho = DEFAULT_TAMANHO,
    mode,
  } = {}) {
    // RESPONDER (interno): conversas das conexões do profissional interno
    // (tenant interno) → /conversas/interna. GENERATOR (cliente): conversas do
    // solicitante externo → /conversas/externa. Sem o split, o interno cai no
    // endpoint externa e não enxerga nenhuma conversa (escopo de solicitante).
    const isResponder = this.resolveMode(mode) === COMUNICACAO_MODE.RESPONDER
    const base = isResponder ? endpoint.conversasInterna() : endpoint.conversas()
    const url = base + formatQueryParams({ pagina, tamanho })
    const r = asResponse(await con.get(url))
    if (r.status === 200 && r.value) {
      const conversas = Array.isArray(r.value.conversas) ? r.value.conversas : []
      return this.ok({ conversas: conversas.map(mapConversaView) }, r.message || 'OK')
    }
    return r
  }

  // REAL (visão externa): GET /v2/api/requisicoes/conversas/externa/<id>/mensagens.
  // Identidade pela sessão; só paginação no request. Mapeia MensagemView[] →
  // shape do atendimento (adiciona id_conversa/reacoes que o store/merge usam).
  async listarMensagens({
    id_conversa,
    pagina = DEFAULT_PAGINA,
    tamanho = DEFAULT_TAMANHO,
    mode,
  } = {}) {
    // RESPONDER (interno): as mensagens vivem no tenant interno (atendimento_*).
    // O endpoint externa valida escopo de SOLICITANTE da sessão → 404 pro
    // interno. Reusa o GET do atendimento, que já devolve o shape consumido
    // pelo store/chat-bubble (sem remapear).
    if (this.resolveMode(mode) === COMUNICACAO_MODE.RESPONDER) {
      const urlInterna =
        endpoint.mensagensAtendimento(id_conversa) +
        formatQueryParams({ per_page: tamanho })
      const ri = asResponse(await con.get(urlInterna))
      if (ri.status === 200 && ri.value) {
        const lista = Array.isArray(ri.value.mensagens) ? ri.value.mensagens : []
        return this.ok({ mensagens: lista }, ri.message || 'OK')
      }
      return ri
    }
    const url =
      endpoint.mensagens(id_conversa) + formatQueryParams({ pagina, tamanho })
    const r = asResponse(await con.get(url))
    if (r.status === 200 && r.value) {
      const mensagens = Array.isArray(r.value.mensagens) ? r.value.mensagens : []
      return this.ok(
        { mensagens: mensagens.map((m) => mapMensagemView(m, id_conversa)) },
        r.message || 'OK'
      )
    }
    return r
  }

  // REAL (visão externa): POST /v2/api/requisicoes/mensagens. O body só leva
  // id_conversa/conteudo/id_solicitacao? (identidade externa vem da sessão). A
  // resposta é EnvioMensagemResult (id_mensagem/canal/status), NÃO uma mensagem
  // pronta — montamos a bolha de saída que o store espera a partir do
  // id_mensagem do servidor + os inputs. whatsapp sem número de saída → 400 no
  // body (sem value); nesse caso devolvemos a resposta crua (sem value mapeado).
  // RESPONDER (interno): resposta do time SEMPRE via plataforma. Usa o endpoint
  // INTERNO do módulo (não o do atendimento), que registra a mensagem como
  // plataforma e NÃO aciona a Evolution — assim não falha por WhatsApp
  // desconectado (o banner já avisa). A resposta já vem no shape do store.
  async _enviarMensagemInterna({ id_conversa, conteudo, is_note, media }) {
    const ri = asResponse(
      await con.post(endpoint.enviarMensagemInterna(), {
        id_conversa,
        conteudo: conteudo || '',
        is_note: !!is_note,
        ...(media || {}),
      })
    )
    if (ri.status === 200 && ri.value) {
      return this.ok(ri.value, ri.message || 'Mensagem enviada.')
    }
    return ri
  }

  async enviarMensagem({
    id_conversa,
    conteudo,
    id_solicitacao,
    is_note,
    tipo,
    media_url,
    media_mime_type,
    media_filename,
    media_size_bytes,
    mode,
  } = {}) {
    // Anexa os campos de mídia ao payload apenas quando há mídia (envio de
    // anexo). Mensagens de texto seguem sem esses campos — o backend decide o
    // transporte (gate whatsapp × plataforma) e grava o `tipo`.
    const media = buildMediaPayload({
      tipo,
      media_url,
      media_mime_type,
      media_filename,
      media_size_bytes,
    })
    if (this.resolveMode(mode) === COMUNICACAO_MODE.RESPONDER) {
      return this._enviarMensagemInterna({ id_conversa, conteudo, is_note, media })
    }
    const payload = { id_conversa, conteudo }
    if (id_solicitacao) payload.id_solicitacao = id_solicitacao
    if (media) Object.assign(payload, media)
    const r = asResponse(await con.post(endpoint.enviarMensagem(), payload))
    if (r.status === 200 && r.value && r.value.id_mensagem) {
      const mensagem = mapMensagemView(
        buildBolhaSaida({ result: r.value, conteudo, media }),
        id_conversa
      )
      return this.ok(mensagem, r.message || 'Mensagem enviada.')
    }
    return r
  }

  // REAL: POST /v2/api/requisicoes/media/upload (multipart). O `con` já
  // converte o objeto em FormData (arquivo + tipo + id_conversa) — NÃO montar
  // FormData manual. Retorna { media_url, media_mime_type, media_filename,
  // media_size_bytes, tipo } que o store mapeia para a mensagem de mídia.
  // `con.post` não expõe progresso granular do upload; sinalizamos 100% na
  // conclusão para o store fechar o ciclo do progress bar (0 → 100 → null).
  async uploadMedia({ id_conversa, tipo, arquivo, onProgress, mode } = {}) {
    if (!id_conversa || !arquivo) {
      return this.buildErrorResponse({
        message: 'Selecione um arquivo válido para enviar.',
        value: null,
      })
    }
    // Atendente (RESPONDER) sobe pelo endpoint interno (escopo por conexão);
    // cliente (GENERATOR) pelo externo (escopo por contato).
    const uploadUrl =
      this.resolveMode(mode) === COMUNICACAO_MODE.RESPONDER
        ? endpoint.mediaUploadInterna()
        : endpoint.mediaUpload()
    const r = asResponse(
      await con.post(uploadUrl, { arquivo, tipo, id_conversa })
    )
    if (typeof onProgress === 'function') onProgress(100)
    if (r.status === 200 && r.value) {
      return this.ok(r.value, r.message || 'Anexo enviado.')
    }
    return r
  }

  async editarMensagem({ id_mensagem, conteudo } = {}) {
    // N/A na visão externa: editar mensagem não exposto pelo backend (a definir).
    await Promise.resolve()
    return this.ok(
      { id: id_mensagem, conteudo: conteudo || '', editada: true },
      'Mensagem editada.'
    )
  }

  async removerMensagem() {
    // N/A na visão externa: remover mensagem não exposto pelo backend (a definir).
    await Promise.resolve()
    return this.ok(null, 'Mensagem removida.')
  }

  async importarHistorico() {
    // N/A na visão externa: importar histórico é feature WhatsApp-interna (a definir).
    await Promise.resolve()
    return this.ok(null, 'Importação de histórico iniciada.')
  }

  async novaConversa() {
    // N/A na visão externa: nova conversa é orquestrada server-side (a definir).
    await Promise.resolve()
    return this.ok(null, 'Conversa criada.')
  }

  // REAL (núcleo): GET /v2/api/requisicoes/conversas/<id_conversa>/solicitacoes.
  // Fonte do painel de detalhes (visão GENERATOR). Identidade externa pela
  // sessão (anti-IDOR); só o id_conversa viaja no path. Lê o shape polimórfico
  // (US-5) e normaliza para { legado, solicitacao|null, solicitacoes[] } via
  // normalizarPainelSolicitacoes (inclui fallback array antigo). Guard de
  // id_conversa antes da URL — sem id não monta request (evita
  // /conversas/undefined/solicitacoes).
  async listarSolicitacoesDaConversa({ id_conversa, mode } = {}) {
    if (!id_conversa) {
      return this.buildErrorResponse({
        message: 'Conversa inválida para carregar as solicitações.',
        value: { legado: false, solicitacao: null, solicitacoes: [] },
      })
    }
    // RESPONDER (interno): o solicitante é resolvido pela conversa no backend
    // (não pela sessão) → endpoint interno. GENERATOR (cliente): endpoint externo.
    const url =
      this.resolveMode(mode) === COMUNICACAO_MODE.RESPONDER
        ? endpoint.solicitacoesDaConversaInterna(id_conversa)
        : endpoint.solicitacoesDaConversa(id_conversa)
    const r = asResponse(await con.get(url))
    if (r.status === 200) {
      return this.ok(normalizarPainelSolicitacoes(r.value), r.message || 'OK')
    }
    return r
  }

  // REAL (RESPONDER/interno): GET conversa 1:1 de UMA solicitação (US-6 — o
  // time interno responde o consultor DENTRO do detalhe da solicitação).
  // 404 é um resultado VÁLIDO (conversa ainda não existe OU sem posse — o
  // backend não distingue de propósito, para não vazar existência): devolve
  // o envelope como veio (value: null), sem tratar como erro. Guard de
  // id_solicitacao antes da URL — sem id não dispara o request.
  async buscarConversaPorSolicitacao({ id_solicitacao } = {}) {
    if (!id_solicitacao) {
      return this.buildErrorResponse({
        message: 'Solicitação inválida para buscar a conversa.',
        value: null,
      })
    }
    const r = asResponse(
      await con.get(endpoint.conversaPorSolicitacaoInterna(id_solicitacao))
    )
    if (r.status === 200 && r.value) {
      return this.ok(mapConversaView(r.value), r.message || 'OK')
    }
    // 404 (sem conversa/sem posse) ou outro status: devolve o envelope do
    // backend tal como veio — a UI decide o que fazer com cada status.
    return r
  }

  // REAL (RESPONDER/interno): POST inicia (find-or-create) a conversa 1:1 de
  // UMA solicitação — trigger explícito do atendente ao clicar em "Responder"
  // no detalhe. 422 quando o tipo não tem conexão vinculada — a mensagem do
  // backend é a que a UI mostra (mensagem acionável). Guard de id_solicitacao
  // antes do payload — sem id não dispara o request.
  async iniciarConversaInterna({ id_solicitacao } = {}) {
    if (!id_solicitacao) {
      return this.buildErrorResponse({
        message: 'Solicitação inválida para iniciar a conversa.',
        value: null,
      })
    }
    const r = asResponse(
      await con.post(endpoint.iniciarConversaInterna(), { id_solicitacao })
    )
    if (r.status === 200 && r.value) {
      return this.ok(mapConversaView(r.value), r.message || 'OK')
    }
    // 422 (sem conexão vinculada) ou 404 (sem posse): devolve o envelope do
    // backend — a mensagem dele é a que a UI mostra.
    return r
  }

  // REAL (núcleo): GET /v2/api/requisicoes/solicitacoes/nota?id_solicitacao=<id>.
  // Lê a nota interna privada de UMA solicitação. Em geral a nota já vem em
  // listarSolicitacoesDaConversa (campo `nota`), então este GET é opcional —
  // disponível para recarregar a nota isolada quando necessário. Guard de
  // id_solicitacao antes da query (não manda ?id_solicitacao=undefined).
  async carregarNotaSolicitacao({ id_solicitacao } = {}) {
    if (!id_solicitacao) {
      return this.buildErrorResponse({
        message: 'Solicitação inválida para carregar a anotação.',
        value: null,
      })
    }
    const url =
      endpoint.notaSolicitacaoGet() + formatQueryParams({ id_solicitacao })
    return asResponse(await con.get(url))
  }

  // REAL (núcleo): PUT /v2/api/requisicoes/solicitacoes/nota, body
  // { id_solicitacao, nota }. Salva a ANOTAÇÃO interna privada do usuário
  // logado ("só você vê"), POR solicitação. A solicitação é identificada pelo
  // `id_solicitacao` (uuid estável; o `codigo` é só display). Identidade
  // externa pela sessão (anti-IDOR). Guard de id_solicitacao antes do payload —
  // sem id não dispara o request (não envia id_solicitacao undefined).
  async salvarNotaSolicitacao({ id_solicitacao, nota } = {}) {
    if (!id_solicitacao) {
      return this.buildErrorResponse({
        message: 'Solicitação inválida para salvar a anotação.',
        value: null,
      })
    }
    const r = asResponse(
      await con.put(endpoint.notaSolicitacao(), {
        id_solicitacao,
        nota: nota || '',
      })
    )
    if (r.status === 200) {
      return this.ok({ id_solicitacao, nota: nota || '' }, r.message || 'Anotação salva.')
    }
    return r
  }

  async reagirMensagem({ id_mensagem, emoji } = {}) {
    // N/A na visão externa: reagir é feature WhatsApp-interna (a definir).
    await Promise.resolve()
    return this.ok(
      { id: id_mensagem, reacoes: [{ emoji, by: 'Você' }] },
      'Reação registrada.'
    )
  }

  async buscarContatos() {
    // N/A na visão externa: buscar contatos é feature WhatsApp-interna (a definir).
    await Promise.resolve()
    return this.ok([], 'Nenhum contato encontrado.')
  }

  // Trigger de criação de conversa: garante (find-or-create) a conversa de cada
  // solicitação ativa do escritório da sessão. POST idempotente — seguro chamar
  // ao abrir a Comunicação. Retorna o resumo { total, criadas, reusadas, ... }.
  async sincronizarConversas() {
    const r = asResponse(await con.post(endpoint.sincronizar(), {}))
    if (r.status === 200) {
      return this.ok(r.value || {}, r.message || 'Conversas sincronizadas.')
    }
    return r
  }

  async pollConversas({ mode } = {}) {
    // Não há endpoint de poll incremental (delta por `since`) no backend. Em vez
    // de um no-op, delegamos pro `listarConversas` real (mode-aware) e
    // devolvemos as conversas como `conversas_atualizadas`. Isso reativa toda a
    // maquinaria do poller: o store faz merge da lista (preview/não-lidas) e o
    // `defaultOnPoll` encontra a conversa aberta entre as atualizadas e puxa o
    // delta das mensagens dela — sem isso o chat aberto fica congelado até
    // fechar/reabrir. Custo aceitável (poucas conversas; merges idempotentes).
    const r = await this.listarConversas({ mode })
    if (r.status === 200 && r.value && Array.isArray(r.value.conversas)) {
      return this.ok(
        { conversas_atualizadas: r.value.conversas },
        'Poll concluído.'
      )
    }
    return this.ok({ conversas_atualizadas: [] }, 'Poll concluído.')
  }

  // REAL: POST /v2/api/requisicoes/conversas/<id>/lida — zera mensagens_nao_lidas
  // da conversa no backend (identidade pela sessão, anti-IDOR). A store já faz o
  // update OTIMISTA (badge some na hora); este POST PERSISTE pra não reaparecer
  // no próximo poll/listarConversas. Enquanto o backend não expõe o endpoint,
  // retorna não-200 e a store ignora (fire-and-forget) — sem quebrar a UI.
  async marcarComoLida({ id_conversa, mode } = {}) {
    if (!id_conversa) {
      return this.buildErrorResponse({
        message: 'Conversa inválida para marcar como lida.',
        value: null,
      })
    }
    // Split externa×interna por modo (igual listarConversas/listarMensagens): no
    // RESPONDER (interno) a conversa vive no inbox do tenant interno → endpoint
    // interno; no GENERATOR (cliente) → endpoint do solicitante. `mode` é opcional;
    // sem ele cai no activeMode (setado por setMode no created da view).
    const isResponder = this.resolveMode(mode) === COMUNICACAO_MODE.RESPONDER
    const url = isResponder
      ? endpoint.marcarLidaInterna(id_conversa)
      : endpoint.marcarLida(id_conversa)
    return asResponse(await con.post(url))
  }

  async atribuirConversa() {
    // N/A na visão externa: atribuir conversa é fluxo de atendimento interno (a definir).
    await Promise.resolve()
    return this.ok({ status: 'em_atendimento' }, 'Conversa atribuída.')
  }

  async fecharConversa() {
    // N/A na visão externa: fechar conversa é fluxo de atendimento interno (a definir).
    await Promise.resolve()
    return this.ok(null, 'Conversa fechada.')
  }

  async escalarConversa() {
    // N/A na visão externa: escalar conversa é fluxo de atendimento interno (a definir).
    await Promise.resolve()
    return this.ok(null, 'Conversa escalada.')
  }

  async transferirConversa() {
    // N/A na visão externa: transferir conversa é fluxo de atendimento interno (a definir).
    await Promise.resolve()
    return this.ok(null, 'Conversa transferida.')
  }

  async mudarStatus({ status } = {}) {
    // N/A na visão externa: mudar status é fluxo de atendimento interno (a definir).
    await Promise.resolve()
    return this.ok({ status: status || null }, 'Status atualizado.')
  }

  async listarRespostasRapidas() {
    // N/A na visão externa: respostas rápidas é feature WhatsApp-interna (a definir).
    await Promise.resolve()
    return this.ok(clone(RESPOSTAS_RAPIDAS_FIXTURE), 'Respostas carregadas.')
  }

  async criarRespostaRapida({ titulo, conteudo } = {}) {
    // N/A na visão externa: criar resposta rápida é feature interna (a definir).
    await Promise.resolve()
    return this.ok(
      { id: 'resp-' + Date.now(), titulo: titulo || '', conteudo: conteudo || '' },
      'Resposta rápida criada.'
    )
  }

  async editarRespostaRapida({ id, titulo, conteudo } = {}) {
    // N/A na visão externa: editar resposta rápida é feature interna (a definir).
    await Promise.resolve()
    return this.ok(
      { id, titulo: titulo || '', conteudo: conteudo || '' },
      'Resposta rápida atualizada.'
    )
  }

  async deletarRespostaRapida() {
    // N/A na visão externa: remover resposta rápida é feature interna (a definir).
    await Promise.resolve()
    return this.ok(null, 'Resposta rápida removida.')
  }

  async getDeliveryStatus() {
    // N/A na visão externa: delivery status é feature WhatsApp-interna (a definir).
    await Promise.resolve()
    return this.ok([], 'Status de entrega carregado.')
  }
}

const apiComunicacao = new ApiComunicacao()

export { apiComunicacao }
export default apiComunicacao
